/**
 * URL-synced filter state — read-modify-write a typed object against the
 * current ?query=string. Pages restore filters on reload, links share
 * filter state, browser back/forward navigates filter history.
 *
 * Convention:
 *   - Array values are emitted as repeated query params (?brand=a&brand=b).
 *   - Empty arrays / "" / undefined are removed from the URL entirely.
 *   - Numbers are stored as strings; the read-back parser is provided by
 *     the caller, so each page picks its own (parseFloat / parseInt /
 *     stay-as-string).
 *
 * Not coupled to any specific filter shape — `Codec` describes how to
 * read each field from URLSearchParams and how to write it back. This
 * lets WheelsList and TiresList share the same hook with different
 * filter sets.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UrlCodec<T> {
  read: (params: URLSearchParams) => T;
  write: (next: T, params: URLSearchParams) => void;
}

export function useUrlState<T>(codec: UrlCodec<T>): [T, (next: T | ((prev: T) => T)) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(() => codec.read(searchParams), [searchParams, codec]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setSearchParams(
        (prev) => {
          const resolved =
            typeof next === 'function'
              ? (next as (p: T) => T)(codec.read(prev))
              : next;
          const merged = new URLSearchParams(prev);
          codec.write(resolved, merged);
          return merged;
        },
        { replace: false },
      );
    },
    [setSearchParams, codec],
  );

  return [value, setValue];
}

// Helpers for the most common URL value shapes — used by per-page codecs.

export function readArray(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).filter((s) => s.length > 0);
}

export function writeArray(params: URLSearchParams, key: string, values: string[] | undefined): void {
  params.delete(key);
  if (!values) return;
  for (const v of values) {
    if (v == null || v === '') continue;
    params.append(key, v);
  }
}

export function readString(params: URLSearchParams, key: string): string {
  return params.get(key) ?? '';
}

export function writeString(params: URLSearchParams, key: string, value: string | undefined): void {
  if (value == null || value === '') {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}

export function readNumber(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key);
  if (raw == null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function writeNumber(params: URLSearchParams, key: string, value: number | undefined): void {
  if (value == null || !Number.isFinite(value)) {
    params.delete(key);
  } else {
    params.set(key, String(value));
  }
}

export function readBool(params: URLSearchParams, key: string): boolean {
  return params.get(key) === 'true';
}

export function writeBool(params: URLSearchParams, key: string, value: boolean | undefined): void {
  if (value === true) {
    params.set(key, 'true');
  } else {
    params.delete(key);
  }
}
